import { useEffect, useRef } from 'react';
import Cleave from 'cleave.js/react';
import classNames from 'classnames';

import { AsteriskIcon } from '../../icons';
import ErrorText from '../ErrorText';

import { getCleaveOptions, getPlaceholder } from './utilities/helpers';

import styles from './_index.module.scss';

export interface InputProps {
  'aria-label'?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  'data-testid'?: string;
  error?: string;
  id?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  max?: number;
  maxLength?: number;
  min?: number;
  onBlur?: (value: number | string) => void;
  onChange?: (value: number | string) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>, value: string) => void;
  placeholder?: string;
  type?: string;
  value?: number | string;
}

const Input = ({
  'aria-label': ariaLabel,
  autoComplete,
  autoFocus,
  className,
  'data-testid': dataTestId,
  error,
  id,
  isDisabled,
  isRequired,
  label,
  max,
  maxLength,
  min,
  onBlur,
  onChange = () => {},
  onPaste,
  placeholder,
  type = 'text',
  value,
}: InputProps) => {
  const formats = ['decimal', 'dollar', 'percentage'];
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, []);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      // @ts-ignore – cleavejs adds rawValue to target
      onBlur(event.target?.rawValue || event.target?.value);
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore – cleavejs adds rawValue to target
    onChange(event.target?.rawValue || event.target?.value);
  };
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (onPaste) {
      // @ts-ignore – window.clipboardData is for IE support, should be updated
      const value = (event.clipboardData || window.clipboardData).getData('text');
      onPaste(event, value);
    }
  };

  const otherClassNames = classNames(
    styles['input'],
    {
      [styles['has-error']]: error,
      [styles['is-percentage']]: type === 'percentage',
    },
    className
  );

  const cleaveOptions = getCleaveOptions(type);
  const props = {
    'aria-label': ariaLabel,
    className: otherClassNames,
    disabled: isDisabled,
    id,
    onBlur: handleBlur,
    onChange: handleChange,
    onPaste: handlePaste,
    placeholder: placeholder || getPlaceholder(type),
    value,
  };

  return (
    <div>
      {label && (
        <label className={styles['input-label']} htmlFor={id}>
          {label}
          {isRequired && <AsteriskIcon />}
        </label>
      )}
      {formats.includes(type) ? (
        <Cleave {...props} options={cleaveOptions} />
      ) : (
        <input
          {...props}
          autoComplete={autoComplete}
          data-testid={dataTestId}
          max={max}
          maxLength={maxLength}
          min={min}
          ref={inputRef}
          type={type}
        />
      )}
      {error && <ErrorText className={styles['input-error']}>{error}</ErrorText>}
    </div>
  );
};

export default Input;
